// apps/web/src/data/queries/notifications.ts
// TanStack Query hooks for the Ops Notices notification center (/api/notifications).
// Drives the global NotificationBell (bell + critical banner + panel) mounted
// in App.tsx across all dashboards.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationsResponse,
  Notification,
  CreateNotificationInput,
  NotificationAction,
} from '@cf-apps/shared';

import { apiFetch } from '../api-client.js';
import { QK }       from '../query-keys.js';

const SIXTY_SEC = 60_000;

/** Global notification feed. Polls every 60s so rule-derived alerts (e.g. econ
 *  calendar running out, cost spike) surface without a manual refresh. Gated by
 *  `enabled` so it never fires on the un-authed /sign-in route. */
export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey:        QK.notifications.list(),
    queryFn:         () => apiFetch<NotificationsResponse>('/api/notifications'),
    staleTime:       SIXTY_SEC,
    refetchInterval: SIXTY_SEC,
    enabled,
  });
}

/** Create a manual notice (the "+ New notice" form). */
export function useCreateNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNotificationInput) =>
      apiFetch<Notification>('/api/notifications', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.notifications.list() });
    },
  });
}

export interface PatchNotificationArgs {
  id:            string;
  action:        NotificationAction;
  snooze_hours?: number;
}

/** Dismiss / snooze / resolve / reopen / unsnooze a notice. */
export function usePatchNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, snooze_hours }: PatchNotificationArgs) =>
      apiFetch<{ ok: true }>(`/api/notifications/${encodeURIComponent(id)}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(snooze_hours !== undefined ? { action, snooze_hours } : { action }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.notifications.list() });
    },
  });
}
